// ================================================================
//  ConsolidatedStatsDialogController
//  Read-only viewer summarizing an interceptor's stats — a "General"
//  tab (all environments, same as the original single-view design)
//  plus one tab per environment scoped to just that environment's own
//  stats. Same underlying data shape as the list load (ic.environments[]
//  with .stats per environment), just re-fetched per-interceptor on a
//  timer instead of relying on the (possibly stale) snapshot passed in
//  when the dialog was opened.
//
//  Tabs render as a vertical nav on the left (see consolidated-stats-
//  dialog.html/.css) rather than Angular Material's horizontal
//  md-tabs, so a long environment list just scrolls the nav column
//  instead of needing md-tabs' pagination arrows.
//
//  Auto-refresh: polls BizInterceptorService.getById() — the same
//  buildEnvironmentsResponse() shape the list endpoint uses — on
//  frontend.consolidated_stats_refresh_interval (central-config,
//  defaults to 10s), silently swapping in fresh data without
//  disturbing the user's current tab selection. The caller
//  (BizInterceptorsTabController) pauses its own list-level
//  auto-refresh for the duration this dialog is open.
//
//  Locals injected:
//    - interceptor : the list row (biz_interceptor_id, biz_interceptor_name,
//                     environments[] with .stats per environment, ...)
// ================================================================

angular.module('bizmetryApp').controller('ConsolidatedStatsDialogController', function (
    $mdDialog, $interval, $mdToast, $scope, interceptor, BizInterceptorService, ConfigurationService, dialogService
) {
    var vm = this;

    vm.interceptor = interceptor;

    // ── restart_required tooltip text — same lead sentence + "Detail: "
    // line shown inline (not behind a hover) on the Environment dialog's
    // own restart badge (see environment-status-dialog.html) — kept as one
    // exact string in both places for consistency. The "\n" here is
    // preserved as a real line break by the white-space:pre-line rule on
    // md-tooltip in consolidated-stats-dialog.css, since Angular
    // Material's default tooltip bubble otherwise clips anything longer
    // than one line. Shared by every restart_required tooltip in this
    // dialog (env-level alert badge, single-env status card,
    // runtime-instance "Requires Restart" pill) so the wording stays
    // consistent across all of them too. ──
    function buildRestartRequiredTooltip(reasonDetail) {
        var lead = 'This interceptor has its configuration changed recently. A cold restart is required for these changes to take effect immediately.';
        return reasonDetail ? lead + '\nDetail: ' + reasonDetail : lead;
    }

    // ── SLA alert flags — is this environment currently mid-breach for a
    // given metric? Reads InterceptorMonitoringConfig's per-environment
    // status list (InterceptorHealthMonitoringJobHandler is the only writer
    // of these — see that job's own javadoc for why status is tracked per
    // environment even though the threshold config itself is interceptor-
    // wide). No entry for an environment means it's implicitly never
    // breached (RESET_CONDITION) — same convention as the job that writes
    // this list. ──
    function findEnvAlert(metricConfig, environmentId) {
        if (!metricConfig || !metricConfig.environment_statuses || !environmentId) { return null; }
        var match = metricConfig.environment_statuses.filter(function (s) {
            return s.environment_id === environmentId;
        })[0];
        return (match && match.status === 'SET_CONDITION') ? match : null;
    }

    function computeForScope(environments, envId, interceptorAvgPingTimeMs, interceptorAvgLoadPressure, interceptorAvgReceiveRateKbMin, interceptorAvgUploadRateKbMin, interceptorAvgCapturedRatePerMin, interceptorAvgFilteredRatePerMin, interceptorAvgDroppedRatePerMin, interceptorAvgUploadedRatePerMin, interceptorAvgActiveWorkerCount, interceptorAvgTransmitBatchSize, interceptorAvgUploadResponsiveness, interceptorAvgHoggingRate, interceptorPressureTrend, interceptorHoggingTrend) {
        var envs = envId === 'all'
            ? environments
            : environments.filter(function (env) { return env.id === envId; });

        // Same InterceptorMonitoringConfig for every environment in scope —
        // resolved once here rather than per-row inside the .map() below.
        var mc = vm.interceptor.monitoring_config || {};

        // ── Per-environment status — status/live/heartbeat/agent don't sum
        // across environments the way counters do, so each environment keeps
        // its own row instead of being collapsed into one value. ──
        var environmentStatuses = envs.map(function (env) {
            var stats = env.stats || {};
            var pressureAlert = findEnvAlert(mc.pressure, env.id);
            var latencyAlert = findEnvAlert(mc.agent_latency, env.id);
            return {
                environment_id: env.id,
                env_name: env.env_name,
                interceptor_status: stats.interceptor_status || 'OFFLINE',
                live_status: !!env.live_status,
                display_status: env.display_status || 'OFFLINE',
                last_state_transition: stats.last_state_transition || null,
                session_started_at: stats.session_started_at || null,
                last_heart_beat: stats.last_heart_beat || null,
                agent_connection: stats.agent_connection || null,
                sync_count: stats.sync_count || 0,
                // SLA breach flags — see findEnvAlert above. *_since is the
                // transition timestamp (epoch millis), null while inactive.
                pressure_alert: !!pressureAlert,
                pressure_alert_since: pressureAlert ? pressureAlert.last_health_change_ts : null,
                latency_alert: !!latencyAlert,
                latency_alert_since: latencyAlert ? latencyAlert.last_health_change_ts : null,
                // How many of this environment's runtime instances are
                // currently RUNNING (the instance-level equivalent of
                // "alive right now") — read straight off env.runtime_instances,
                // no recomputation; status itself is written by /init,
                // /sync and BizInterceptorHealthCheckJobHandler, never
                // resolved fresh here (same split as the instance list
                // itself, see the 'instances' aggregation below).
                instances_online: (env.runtime_instances || [])
                    .filter(function (ri) { return ri.status === 'RUNNING'; }).length,
                // Already the average, computed server-side on the fly over
                // a trailing window — same "already averaged, null means no
                // sample" shape as avg_ping_time_ms/avg_load_pressure below
                // (see BizInterceptorServiceImpl#resolveWindowedAverages).
                // Replaces the old live-snapshot SUM across
                // env.runtime_instances[].
                avg_active_worker_count: (stats.avg_active_worker_count === null || stats.avg_active_worker_count === undefined)
                    ? null : stats.avg_active_worker_count,
                // Same shape as avg_active_worker_count above, for the
                // backpressure auto-tuner's effective sync batch size.
                avg_transmit_batch_size: (stats.avg_transmit_batch_size === null || stats.avg_transmit_batch_size === undefined)
                    ? null : stats.avg_transmit_batch_size,
                // Server-computed (see BizInterceptorEnvironmentDTO#restartRequired)
                // — true while a config change couldn't hot-apply (currently
                // only a buffer_max_size change) on at least one of this
                // environment's runtime instances. An OR across every
                // instance, not just one: stays true until EVERY replica has
                // actually been restarted, not just whichever one syncs
                // next — see InterceptorHostContext#getRestartRequired()'s
                // own javadoc for why. Not recomputed here, just read
                // straight off the model, same as every other status field
                // on this row.
                restart_required: !!env.restart_required,
                // Full tooltip text (lead sentence + specific reason) — see
                // buildRestartRequiredTooltip below. First flagged
                // instance's reason is enough: every flagged instance of
                // the same environment carries the same one (they were all
                // flagged together by the same config-save, see
                // BizInterceptorServiceImpl#patch).
                restart_required_reason: env.restart_required
                    ? buildRestartRequiredTooltip((env.runtime_instances || [])
                        .filter(function (ri) { return !!ri.restart_required; })
                        .map(function (ri) { return ri.restart_required_reason; })[0])
                    : null,
                // Server-computed (see BizInterceptorEnvironmentDTO#uploadFailing)
                // — an OR across every instance's own upload_failing, same
                // shape as restart_required above: stays true until every
                // failing replica's Agent connection recovers, not just
                // whichever one syncs next. Not recomputed here, just read
                // straight off the model — see environment-status-dialog's
                // EnvironmentStatusDialogController for the same read.
                upload_failing: !!env.upload_failing,
                upload_failing_since_ts: env.upload_failing_since_ts || null,
                last_upload_error: env.last_upload_error || null,
                // Already the average, computed server-side on the fly over
                // a trailing window — see BizInterceptorServiceImpl#resolveAvgPingTimeMs.
                // null (not 0) when no ping sample landed in that window.
                avg_ping_time_ms: (stats.avg_ping_time_ms === null || stats.avg_ping_time_ms === undefined)
                    ? null : stats.avg_ping_time_ms,
                // Same "already averaged server-side over a trailing window,
                // null (not 0) means no sample" shape as avg_ping_time_ms
                // above — see BizInterceptorServiceImpl#resolveAvgLoadPressure.
                avg_load_pressure: (stats.avg_load_pressure === null || stats.avg_load_pressure === undefined)
                    ? null : stats.avg_load_pressure,
                // Same "already averaged server-side over a trailing window,
                // null (not 0) means no sample" shape as avg_ping_time_ms/
                // avg_load_pressure above — see
                // BizInterceptorServiceImpl#resolveAvgReceiveRateKbMin/
                // #resolveAvgUploadRateKbMin. Replaces the old
                // bytes_received/bytes_uploaded lifetime totals.
                avg_receive_rate_kb_min: (stats.avg_receive_rate_kb_min === null || stats.avg_receive_rate_kb_min === undefined)
                    ? null : stats.avg_receive_rate_kb_min,
                avg_upload_rate_kb_min: (stats.avg_upload_rate_kb_min === null || stats.avg_upload_rate_kb_min === undefined)
                    ? null : stats.avg_upload_rate_kb_min,
                // Same null-preserving shape as the rates above — see
                // BizInterceptorServiceImpl#resolveAvgCapturedRatePerMin/
                // #resolveAvgFilteredRatePerMin/#resolveAvgDroppedRatePerMin/
                // #resolveAvgUploadedRatePerMin. This row shows the RATE only
                // — the lifetime cumulative total for the same 4 dimensions
                // (captured/filtered/dropped/uploaded) still lives in
                // general.total_frames_* below, shown in the separate
                // "Lifetime Totals" tiles.
                avg_captured_rate_per_min: (stats.avg_captured_rate_per_min === null || stats.avg_captured_rate_per_min === undefined)
                    ? null : stats.avg_captured_rate_per_min,
                avg_filtered_rate_per_min: (stats.avg_filtered_rate_per_min === null || stats.avg_filtered_rate_per_min === undefined)
                    ? null : stats.avg_filtered_rate_per_min,
                avg_dropped_rate_per_min: (stats.avg_dropped_rate_per_min === null || stats.avg_dropped_rate_per_min === undefined)
                    ? null : stats.avg_dropped_rate_per_min,
                avg_uploaded_rate_per_min: (stats.avg_uploaded_rate_per_min === null || stats.avg_uploaded_rate_per_min === undefined)
                    ? null : stats.avg_uploaded_rate_per_min,
                // Server-computed ratios (see BizInterceptorServiceImpl#
                // resolveAvgUploadResponsiveness/#resolveAvgHoggingRate) —
                // replaces what used to be computed client-side here.
                avg_upload_responsiveness: (stats.avg_upload_responsiveness === null || stats.avg_upload_responsiveness === undefined)
                    ? null : stats.avg_upload_responsiveness,
                avg_hogging_rate: (stats.avg_hogging_rate === null || stats.avg_hogging_rate === undefined)
                    ? null : stats.avg_hogging_rate,
                // 'UP'/'DOWN'/'FLAT'/'UNKNOWN' — see BizInterceptorServiceImpl#
                // computeTrend/TrendDirection. Falls back to 'UNKNOWN' (no
                // arrow shown) rather than null, since the template's
                // vm.trendIcon() only needs to special-case one "no data"
                // value instead of two.
                pressure_trend: stats.pressure_trend || 'UNKNOWN',
                hogging_trend: stats.hogging_trend || 'UNKNOWN'
            };
        });

        var totalEnvironments = environmentStatuses.length;
        var onlineCount = environmentStatuses.filter(function (e) { return e.interceptor_status === 'ONLINE'; }).length;
        var liveCount = environmentStatuses.filter(function (e) { return e.live_status; }).length;

        // Interceptor-wide total — same value the list/summary API returns as
        // biz_interceptor.sync_count, recomputed here for consistency with the
        // rest of this dialog's client-side aggregation.
        var totalSyncCount = environmentStatuses.reduce(function (sum, e) { return sum + e.sync_count; }, 0);

        var latestHeartBeat = environmentStatuses.reduce(function (latest, e) {
            return (e.last_heart_beat && (!latest || e.last_heart_beat > latest)) ? e.last_heart_beat : latest;
        }, null);

        // ── Aggregated general stats — plain sum of each environment's
        // lifetime counters (a no-op sum when scoped to a single
        // environment). Feeds both packet-distribution-pie (captured/
        // filtered/dropped) and the "Lifetime Totals" summary tiles
        // (all 6 fields) — these are genuine cumulative totals, so a plain
        // sum across environments is correct here, unlike the AVG rate
        // fields above (avg_receive_rate_kb_min/avg_captured_rate_per_min/
        // etc.), which need the backend's weighted rollup instead since
        // summing per-environment AVERAGES wouldn't produce a correct
        // combined average. ──
        var general = envs.reduce(function (acc, env) {
            var g = (env.stats && env.stats.general) || {};
            acc.total_frames_captured += g.total_frames_captured || 0;
            acc.total_frames_dropped  += g.total_frames_dropped  || 0;
            acc.total_frames_filtered += g.total_frames_filtered || 0;
            acc.total_frames_uploaded += g.total_frames_uploaded || 0;
            acc.bytes_received        += g.bytes_received || 0;
            acc.bytes_uploaded        += g.bytes_uploaded || 0;
            return acc;
        }, { total_frames_captured: 0, total_frames_dropped: 0, total_frames_filtered: 0, total_frames_uploaded: 0, bytes_received: 0, bytes_uploaded: 0 });

        // ── Aggregated per-operation stats — same operation can appear in
        // several environments (one entry per env it was captured in), so
        // counters are summed keyed by operation_id. A no-op grouping when
        // scoped to a single environment. ──
        var operationsById = {};
        envs.forEach(function (env) {
            var ops = env.stats && env.stats.operations_scope === 'non_discovery' ? (env.stats.operations || []) : [];
            ops.forEach(function (op) {
                var key = op.operation_id || (op.collection_id + ':' + op.operation_name);
                if (!operationsById[key]) {
                    operationsById[key] = {
                        operation_id: op.operation_id,
                        collection_name: op.collection_name,
                        operation_name: op.operation_name,
                        type: null,
                        method: null,
                        path: null,
                        collection_version: null,
                        live: false,
                        total_frames_captured: 0,
                        total_frames_dropped: 0,
                        total_frames_uploaded: 0,
                        total_frames_filtered: 0,
                        bytes_received: 0,
                        bytes_uploaded: 0
                    };
                }
                var agg = operationsById[key];
                // Same environment can resolve a different direction than
                // another (e.g. live as INBOUND in staging, OUTBOUND in
                // prod) — union them the same way the backend merges modes
                // across live sources for a single environment. An operation
                // live in ANY environment counts as live for the 'all' scope
                // — matches "is this used anywhere" better than requiring
                // every environment to agree.
                if (op.type) {
                    agg.type = (!agg.type || agg.type === op.type) ? op.type : 'INBOUND_OUTBOUND';
                }
                // Method/path/collection_version are properties of the
                // operation itself, not of any one environment — same value
                // everywhere it's live, so the first non-null wins rather
                // than needing a union like type/counters above. These (and
                // type above) are sticky on the backend now — last-known
                // value, not blank once the operation stops being live — so
                // `live` below is the actual live/inactive signal, unioned
                // across environments same as type used to be.
                if (op.method && !agg.method) { agg.method = op.method; }
                if (op.path && !agg.path) { agg.path = op.path; }
                if (op.collection_version && !agg.collection_version) { agg.collection_version = op.collection_version; }
                if (op.live) { agg.live = true; }
                agg.total_frames_captured += op.total_frames_captured || 0;
                agg.total_frames_dropped  += op.total_frames_dropped  || 0;
                agg.total_frames_uploaded += op.total_frames_uploaded || 0;
                agg.total_frames_filtered += op.total_frames_filtered || 0;
                agg.bytes_received        += op.bytes_received || 0;
                agg.bytes_uploaded        += op.bytes_uploaded || 0;
            });
        });
        // Keep every monitored endpoint, including those with no traffic yet.
        // This flag describes accumulated counters, not current throughput or liveness.
        var operations = Object.keys(operationsById).map(function (key) {
            var op = operationsById[key];
            op.has_traffic = ['total_frames_captured', 'total_frames_uploaded',
                'total_frames_filtered', 'total_frames_dropped', 'bytes_received', 'bytes_uploaded']
                .some(function (counter) { return (op[counter] || 0) > 0; });
            op.traffic_order = op.has_traffic ? 0 : 1;
            return op;
        }).sort(function (a, b) {
            return a.traffic_order - b.traffic_order ||
                (b.total_frames_captured || 0) - (a.total_frames_captured || 0) ||
                String(a.operation_name || '').localeCompare(String(b.operation_name || ''));
        });

        // Discovery totals describe the latest capture in environments with the toggle ON.
        var discovery = { enabled: false, discovered_frames: 0, discovered_apis: 0 };
        var discoveredKeys = Object.create(null);
        var discoveryTraffic = Object.create(null);
        envs.forEach(function (env) {
            var settings = (env.parameters || {}).parameters || {};
            var stats = (env.stats || {}).discovery || {};
            if (settings.discovery_mode) {
                discovery.enabled = true;
                discovery.discovered_frames += stats.discovered_frames || 0;
                Object.keys(stats.apis || {}).forEach(function (key) { discoveredKeys[key] = true; });
                Object.keys(stats.traffic || {}).forEach(function (trafficKey) {
                    var api = stats.traffic[trafficKey];
                    // The interceptor keys traffic by live_mode too, so an API whose live mapping
                    // changed mid-capture arrives as two entries (Known + Live). Show one row per
                    // API: counts summed, Live if any of its captures matched an active Biz Event.
                    var key = [api.method, api.scheme, api.server_name, api.server_port, api.path,
                        api.collection_id, api.operation_id, api.collection_version, api.operation_version, api.direction].join('|');
                    if (!discoveryTraffic[key]) {
                        discoveryTraffic[key] = angular.extend({}, api, {
                            row_key: key, discovered_api: api.api_operation_found === false,
                            live_mode: false, frames: 0, bytes_captured: 0
                        });
                    }
                    discoveryTraffic[key].live_mode = discoveryTraffic[key].live_mode || !!api.live_mode;
                    discoveryTraffic[key].frames += api.frames || 0;
                    discoveryTraffic[key].bytes_captured += api.bytes_captured || 0;
                });
            }
        });
        discovery.discovered_apis = Object.keys(discoveredKeys).length;
        discovery.traffic = Object.keys(discoveryTraffic).map(function (key) { return discoveryTraffic[key]; });
        operations.forEach(function (op) { op.row_key = 'operation:' + op.operation_id; });

        // ── Runtime instances — one row per interceptor PROCESS currently
        // (or previously) reporting into scope, flattened across every
        // environment in scope. Unlike operations, these are NEVER merged
        // across environments (a process only ever belongs to one) — each
        // keeps env_name attached so the 'all'/General view can tell them
        // apart; a no-op for a single-environment scope, just redundant
        // there (same env_name on every row), which is simpler and more
        // robust than a dynamic column layout that hides/shows per scope. ──
        var instances = [];
        envs.forEach(function (env) {
            (env.runtime_instances || []).forEach(function (ri) {
                // status (RUNNING/STALE/OFFLINE) already rides on `ri` from
                // the backend — nothing computed client-side here, just
                // env_id/env_name attached so the 'all'/General view can
                // tell instances from different environments apart.
                instances.push(angular.extend({}, ri, {
                    env_id: env.id,
                    env_name: env.env_name
                }));
            });
        });
        // Most-recently-seen first by default — "who's actually running
        // right now" is more useful at a glance than an arbitrary order.
        instances.sort(function (a, b) { return (b.last_sync_ts || 0) - (a.last_sync_ts || 0); });

        // Summary-level restart_required rollup, scoped the same way as
        // everything else here (interceptor-wide for 'all', that one
        // environment's own row for a single-environment tab) — how many
        // ENVIRONMENTS in scope need a restart, each already itself an OR
        // across that environment's own instances (see
        // environmentStatuses[].restart_required above) — drives the badge
        // in the Stats summary row (see consolidated-stats-dialog.html) so
        // this is visible without having to open the Runtime Instances
        // sub-tab.
        var restartRequiredEnvironments = environmentStatuses.filter(function (e) { return e.restart_required; });

        // Same rollup shape as restartRequiredEnvironments above, for the
        // upload-failing badge in the Stats summary row — how many
        // ENVIRONMENTS in scope have at least one instance currently
        // failing to upload frames to its Agent (see
        // environmentStatuses[].upload_failing above).
        var uploadFailingEnvironments = environmentStatuses.filter(function (e) { return e.upload_failing; });

        return {
            discovery: discovery,
            scopeLabel: envId === 'all' ? 'all environments' : (environmentStatuses[0] || {}).env_name,
            environmentStatuses: environmentStatuses,
            instances: instances,
            // Runtime Instances tab badge — how many are currently
            // RUNNING, not the total (which includes STALE/OFFLINE rows
            // still shown for visibility) — same "live" framing as the
            // environment table's own Live Instances column.
            liveInstancesCount: instances.filter(function (ri) { return ri.status === 'RUNNING'; }).length,
            // Interceptor Live Trends' "Active Workers" tile, next to
            // Hogging Rate — same 'all' vs single-environment split as
            // avgLoadPressure/avgHoggingRate below: 'all' reads the real
            // interceptor-wide average from the backend (BizInterceptorDTO#
            // avg_active_worker_count, itself a SUM of each environment's own
            // average — a capacity dimension, not a utilization gauge, same
            // reasoning as the frame-rate sums there), single-environment
            // scope reads that one environment's own already-computed
            // average. Replaces the old live-snapshot SUM across
            // env.runtime_instances[].
            avgActiveWorkerCount: envId === 'all'
                ? interceptorAvgActiveWorkerCount
                : (environmentStatuses[0] || {}).avg_active_worker_count,
            // Same shape as avgActiveWorkerCount above, for the "Transmit
            // Batch Size" tile.
            avgTransmitBatchSize: envId === 'all'
                ? interceptorAvgTransmitBatchSize
                : (environmentStatuses[0] || {}).avg_transmit_batch_size,
            restartRequiredCount: restartRequiredEnvironments.length,
            uploadFailingCount: uploadFailingEnvironments.length,
            // Single-environment tabs only ever have one row — expose it
            // directly so the template can show a compact status strip
            // instead of a 1-row table.
            envStatus: envId === 'all' ? null : (environmentStatuses[0] || null),
            totalEnvironments: totalEnvironments,
            onlineCount: onlineCount,
            liveCount: liveCount,
            totalSyncCount: totalSyncCount,
            // 'all': the real interceptor-wide average from the backend
            // (BizInterceptorDTO#avg_ping_time_ms) — NOT a mean of the
            // per-environment averages below, which would skew toward
            // environments with fewer samples. Single-environment scope:
            // that environment's own already-computed average.
            avgPingTimeMs: envId === 'all'
                ? interceptorAvgPingTimeMs
                : (environmentStatuses[0] || {}).avg_ping_time_ms,
            // Same 'all' vs single-environment split as avgPingTimeMs above.
            avgLoadPressure: envId === 'all'
                ? interceptorAvgLoadPressure
                : (environmentStatuses[0] || {}).avg_load_pressure,
            // Same 'all' vs single-environment split as avgPingTimeMs/
            // avgLoadPressure above — replaces the old summed
            // general.bytes_received/bytes_uploaded totals.
            avgReceiveRateKbMin: envId === 'all'
                ? interceptorAvgReceiveRateKbMin
                : (environmentStatuses[0] || {}).avg_receive_rate_kb_min,
            avgUploadRateKbMin: envId === 'all'
                ? interceptorAvgUploadRateKbMin
                : (environmentStatuses[0] || {}).avg_upload_rate_kb_min,
            // Same 'all' vs single-environment split as avgReceiveRateKbMin/
            // avgUploadRateKbMin above — replaces the old summed
            // general.total_frames_captured/filtered/dropped totals shown
            // as tiles (the SUMMED general.* fields above stay as-is, since
            // the packet-distribution-pie chart still needs real totals).
            avgCapturedRatePerMin: envId === 'all'
                ? interceptorAvgCapturedRatePerMin
                : (environmentStatuses[0] || {}).avg_captured_rate_per_min,
            avgFilteredRatePerMin: envId === 'all'
                ? interceptorAvgFilteredRatePerMin
                : (environmentStatuses[0] || {}).avg_filtered_rate_per_min,
            avgDroppedRatePerMin: envId === 'all'
                ? interceptorAvgDroppedRatePerMin
                : (environmentStatuses[0] || {}).avg_dropped_rate_per_min,
            avgUploadedRatePerMin: envId === 'all'
                ? interceptorAvgUploadedRatePerMin
                : (environmentStatuses[0] || {}).avg_uploaded_rate_per_min,
            // Same 'all' vs single-environment split as the rates above —
            // see BizInterceptorServiceImpl#resolveAvgUploadResponsiveness/
            // #resolveAvgHoggingRate/#applyEnvironments' "worst wins"
            // rollup for the 2 trend enums.
            avgUploadResponsiveness: envId === 'all'
                ? interceptorAvgUploadResponsiveness
                : (environmentStatuses[0] || {}).avg_upload_responsiveness,
            avgHoggingRate: envId === 'all'
                ? interceptorAvgHoggingRate
                : (environmentStatuses[0] || {}).avg_hogging_rate,
            pressureTrend: envId === 'all'
                ? (interceptorPressureTrend || 'UNKNOWN')
                : (environmentStatuses[0] || {}).pressure_trend,
            hoggingTrend: envId === 'all'
                ? (interceptorHoggingTrend || 'UNKNOWN')
                : (environmentStatuses[0] || {}).hogging_trend,
            latestHeartBeat: latestHeartBeat,
            general: general,
            operations: operations
        };
    }

    // Rebuilds tabs + tabData from a fresh environments[] snapshot, keeping
    // the user's current tab selection when it still exists (falls back to
    // 'all' the first time, or if that environment disappeared).
    function rebuild(freshInterceptor) {
        vm.interceptor = freshInterceptor;
        var environments = freshInterceptor.environments || [];

        vm.tabs = [{ id: 'all', name: 'General' }].concat(
            environments.map(function (env) { return { id: env.id, name: env.env_name }; })
        );

        vm.tabData = {};
        vm.tabs.forEach(function (tab) {
            vm.tabData[tab.id] = computeForScope(environments, tab.id, freshInterceptor.avg_ping_time_ms, freshInterceptor.avg_load_pressure, freshInterceptor.avg_receive_rate_kb_min, freshInterceptor.avg_upload_rate_kb_min, freshInterceptor.avg_captured_rate_per_min, freshInterceptor.avg_filtered_rate_per_min, freshInterceptor.avg_dropped_rate_per_min, freshInterceptor.avg_uploaded_rate_per_min, freshInterceptor.avg_active_worker_count, freshInterceptor.avg_transmit_batch_size, freshInterceptor.avg_upload_responsiveness, freshInterceptor.avg_hogging_rate, freshInterceptor.pressure_trend, freshInterceptor.hogging_trend);
        });

        if (!vm.selectedTabId || !vm.tabData[vm.selectedTabId]) {
            vm.selectedTabId = 'all';
        }
        vm.activeData = vm.tabData[vm.selectedTabId];
        if (vm.selectedSubTab === 'discovery' && !vm.activeData.discovery.enabled) { vm.selectedSubTab = 'stats'; }
    }

    // Render immediately with whatever locals the list already had (avoids a
    // blank dialog while the fresh fetch below is in flight), then refresh
    // right away — the list this dialog was opened from can be up to
    // refreshInterval stale itself, so without this the dialog would show
    // that same stale snapshot until its own first auto-refresh tick fires.
    rebuild(interceptor);
    refreshFromServer();

    vm.selectTab = function (tabId) {
        vm.selectedTabId = tabId;
        vm.activeData = vm.tabData[tabId];
        if (vm.selectedSubTab === 'discovery' && !vm.activeData.discovery.enabled) { vm.selectedSubTab = 'stats'; }
    };

    // ========================================
    // SUB-TABS — "Stats" (general numbers + environment list + pie chart),
    // "Operations" (per-operation breakdown, sortable), and "Runtime
    // Instances" (per-process host/JVM + live cpu/buffer, sortable). Kept
    // separate from vm.selectedTabId/vm.selectTab (which environment is
    // scoped) so switching environments doesn't reset which sub-tab the
    // user is on.
    // ========================================

    vm.selectedSubTab = 'stats';
    vm.selectSubTab = function (subTab) {
        vm.selectedSubTab = subTab === 'discovery' && !vm.activeData.discovery.enabled ? 'stats' : subTab;
    };
    // Discovery search: matches the endpoint (method + path) or the operation name.
    vm.discoverySearch = '';
    vm.discoveryMatches = function (api) {
        var term = (vm.discoverySearch || '').trim().toLowerCase();
        if (!term) { return true; }
        var endpoint = ((api.method || '') + ' ' + (api.path || '')).toLowerCase();
        return endpoint.indexOf(term) !== -1 ||
               (api.path || '').toLowerCase().indexOf(term) !== -1 ||
               (api.operation_name || '').toLowerCase().indexOf(term) !== -1;
    };

    vm.discoverySortKey = 'frames';
    vm.discoverySortReverse = true;
    vm.discoverySort = ['-discovered_api', '-frames', 'path', 'row_key'];
    vm.setDiscoverySort = function (key) {
        vm.discoverySortReverse = vm.discoverySortKey === key ? !vm.discoverySortReverse
            : (key === 'frames' || key === 'bytes_captured');
        vm.discoverySortKey = key;
        vm.discoverySort = ['-discovered_api', (vm.discoverySortReverse ? '-' : '') + key, 'path', 'row_key'];
    };
    vm.discoverySortIcon = function (key) {
        return vm.discoverySortKey === key ? (vm.discoverySortReverse ? 'arrow_downward' : 'arrow_upward') : null;
    };

    // ========================================
    // OPERATIONS SORTING — click a column header to sort by it; click again
    // to flip direction. Numeric columns default to DESC on first click
    // (the biggest captured/filtered/dropped counts are what's actionable),
    // operation name defaults to ASC (alphabetical reads naturally).
    // ========================================

    vm.opSortKey = 'total_frames_captured';
    vm.opSortReverse = true;
    vm.operationSort = ['traffic_order', '-total_frames_captured', 'operation_name', 'row_key'];

    var ASC_BY_DEFAULT = { operation_name: true, collection_name: true, type: true, path: true, collection_version: true };

    vm.setOperationSort = function (key) {
        if (vm.opSortKey === key) {
            vm.opSortReverse = !vm.opSortReverse;
        } else {
            vm.opSortKey = key;
            vm.opSortReverse = !ASC_BY_DEFAULT[key];
        }
        vm.operationSort = ['traffic_order', (vm.opSortReverse ? '-' : '') + key, 'operation_name', 'row_key'];
    };

    vm.operationSortIcon = function (key) {
        if (vm.opSortKey !== key) { return null; }
        return vm.opSortReverse ? 'arrow_downward' : 'arrow_upward';
    };

    // ========================================
    // RUNTIME INSTANCES SORTING — same click-header pattern as Operations
    // above. Defaults to most-recently-synced first (numeric-like: DESC).
    // ========================================

    vm.riSortKey = 'last_sync_ts';
    vm.riSortReverse = true;

    var RI_ASC_BY_DEFAULT = { env_name: true, hostname: true, ip_address: true, os_name: true, runtime_version: true, status: true };

    vm.setInstanceSort = function (key) {
        if (vm.riSortKey === key) {
            vm.riSortReverse = !vm.riSortReverse;
        } else {
            vm.riSortKey = key;
            vm.riSortReverse = !RI_ASC_BY_DEFAULT[key];
        }
    };

    vm.instanceSortIcon = function (key) {
        if (vm.riSortKey !== key) { return null; }
        return vm.riSortReverse ? 'arrow_downward' : 'arrow_upward';
    };

    // RUNNING/STALE/OFFLINE comes straight from the backend (see
    // InterceptorHostContext#status / BizInterceptorServiceImpl#resolveRuntimeInstanceStatus)
    // — resolved fresh there on every read from last_sync_ts vs the
    // platform's configured thresholds, not recomputed here. Same badge
    // component as the environment status strip above, just its own
    // 3-state palette (green/yellow/red already exist there for
    // online/standby/offline — reused directly for running/stale/offline).
    var INSTANCE_STATUS_BADGE_CLASSES = {
        RUNNING: 'csd-status-badge--online',
        STALE: 'csd-status-badge--standby',
        OFFLINE: 'csd-status-badge--offline'
    };
    var INSTANCE_STATUS_BADGE_LABELS = {
        RUNNING: 'Running',
        STALE: 'Drained',
        OFFLINE: 'Offline'
    };
    // restart_required overrides RUNNING/STALE/OFFLINE entirely — a
    // "Requires Restart" instance (running with a config change it can't
    // hot-apply) is worth flagging regardless of its liveness status, so
    // this takes priority over the three functions below rather than
    // being a 4th case inside them.
    vm.instanceStatusBadgeClass = function (ri) {
        if (ri && ri.restart_required) { return 'csd-status-badge--restart'; }
        return INSTANCE_STATUS_BADGE_CLASSES[ri && ri.status] || 'csd-status-badge--offline';
    };
    vm.instanceStatusBadgeLabel = function (ri) {
        if (ri && ri.restart_required) { return 'Requires Restart'; }
        return INSTANCE_STATUS_BADGE_LABELS[ri && ri.status] || 'Unknown';
    };
    vm.instanceStatusTooltip = function (ri) {
        return (ri && ri.restart_required) ? buildRestartRequiredTooltip(ri.restart_required_reason) : null;
    };

    vm.instanceOsLabel = function (ri) {
        if (!ri || !ri.os_name) { return '—'; }
        return ri.os_name + (ri.os_arch ? ' (' + ri.os_arch + ')' : '');
    };

    // runtime_technology/runtime_version are deliberately generic on the
    // backend (not java_*) — this SDK is Java-only today, but the platform
    // will eventually have interceptor SDKs for other languages too, each
    // reporting into these same fields. Showing the technology alongside
    // the version (not version alone) keeps this column meaningful once
    // that happens — "Java 17.0.19" vs. some other stack's own version
    // string, not just an ambiguous bare number.
    vm.instanceRuntimeLabel = function (ri) {
        if (!ri || !ri.runtime_version) { return '—'; }
        return (ri.runtime_technology ? ri.runtime_technology + ' ' : '') + ri.runtime_version;
    };

    // ========================================
    // AUTO-REFRESH — re-fetches this single interceptor (same
    // buildEnvironmentsResponse() shape the list uses) on an interval so
    // live_status/stats don't go stale while the dialog is open. Silent —
    // no loading spinner, data just swaps in place.
    // ========================================

    var refreshInterval = ConfigurationService.getFromCache('frontend.consolidated_stats_refresh_interval') || 10000;

    function refreshFromServer() {
        BizInterceptorService.getById(vm.interceptor.biz_interceptor_id, { skipLoader: true })
            .then(function (response) {
                if (response && response.data) { rebuild(response.data); }
            })
            .catch(function (error) {
                console.error('[ConsolidatedStatsDialog] Error refreshing interceptor stats:', error);
            });
    }

    var autoRefreshTimer = $interval(refreshFromServer, refreshInterval);

    $scope.$on('$destroy', function () {
        if (autoRefreshTimer) { $interval.cancel(autoRefreshTimer); }
    });

    // ========================================
    // RESET ALL — Lifetime Totals section. Zeroes Total Captured/Filtered/
    // Dropped/Uploaded, both byte counters, and every operation's own
    // breakdown — for every environment when the "General" tab is active,
    // or just the currently-selected environment otherwise. Irreversible, so
    // one confirmation modal comes before actually calling the backend.
    // ========================================
    vm.resetLifetimeStats = function () {
        var isAll = vm.selectedTabId === 'all';
        var scopeText = isAll ? 'every environment' : ('the "' + vm.activeData.scopeLabel + '" environment');

        dialogService.showDecisionDialog(
            'Reset Lifetime Totals',
            'You are about to permanently reset the Lifetime Totals — Total Captured, Filtered, Dropped, ' +
                'Uploaded, both byte counters, and every operation\'s own breakdown — for <strong>' + scopeText +
                '</strong> back to zero. This does not affect Live Trends or any configuration, and cannot be undone.',
            { goBack: 'Cancel', proceed: 'Reset' }
        ).then(function (decision) {
            if (decision !== 'proceed') { return; }

            var environmentId = isAll ? null : vm.selectedTabId;
            BizInterceptorService.resetLifetimeStats(vm.interceptor.biz_interceptor_id, environmentId)
                .then(function () {
                    $mdToast.show(
                        $mdToast.simple()
                            .textContent('Lifetime totals reset successfully')
                            .position('top right')
                            .hideDelay(3000)
                    );
                    refreshFromServer();
                })
                .catch(function (err) {
                    console.error('[ConsolidatedStatsDialog] Error resetting lifetime stats:', err);
                    dialogService.showErrorDialog(
                        'Reset Failed',
                        (err.data && err.data.message) || 'Could not reset lifetime totals.'
                    );
                });
        });
    };

    // ========================================
    // RESET ALL — Discovery tab. Starts a new discovery capture: Discovered
    // Frames, Discovered APIs and the traffic table back to zero — for every
    // environment when the "General" tab is active, or just the currently-
    // selected environment otherwise (same scoping and confirmation modal as
    // the Lifetime Totals' Reset All above).
    // ========================================
    vm.resetDiscoveryStats = function () {
        var isAll = vm.selectedTabId === 'all';
        var scopeText = isAll ? 'every environment' : ('the "' + vm.activeData.scopeLabel + '" environment');

        dialogService.showDecisionDialog(
            'Reset Discovery Statistics',
            'You are about to permanently reset the Discovery statistics — Discovered Frames, Discovered APIs ' +
                'and every row of the discovery traffic — for <strong>' + scopeText + '</strong>, starting a new ' +
                'discovery capture. Lifetime Totals, Live Trends and the configuration are not affected. ' +
                'This cannot be undone.',
            { goBack: 'Cancel', proceed: 'Reset' }
        ).then(function (decision) {
            if (decision !== 'proceed') { return; }

            var environmentId = isAll ? null : vm.selectedTabId;
            BizInterceptorService.resetDiscoveryStats(vm.interceptor.biz_interceptor_id, environmentId)
                .then(function () {
                    $mdToast.show(
                        $mdToast.simple()
                            .textContent('Discovery statistics reset successfully')
                            .position('top right')
                            .hideDelay(3000)
                    );
                    refreshFromServer();
                })
                .catch(function (err) {
                    console.error('[ConsolidatedStatsDialog] Error resetting discovery stats:', err);
                    dialogService.showErrorDialog(
                        'Reset Failed',
                        (err.data && err.data.message) || 'Could not reset discovery statistics.'
                    );
                });
        });
    };

    // ========================================
    // RESET ALL — Operations tab. Empties the per-operation breakdown (every
    // row of the Operations table) — for every environment when the "General"
    // tab is active, or just the currently-selected environment otherwise.
    // Rows come back as new traffic is captured for them.
    // ========================================
    vm.resetOperationStats = function () {
        var isAll = vm.selectedTabId === 'all';
        var scopeText = isAll ? 'every environment' : ('the "' + vm.activeData.scopeLabel + '" environment');

        dialogService.showDecisionDialog(
            'Reset Live Endpoint Statistics',
            'You are about to permanently reset the Live Endpoints statistics — their ' +
                'Captured, Uploaded, Filtered, Dropped and byte counters — for <strong>' + scopeText + '</strong>. ' +
                'Monitored Live Endpoints remain listed with zero counters until traffic is captured again. Lifetime Totals, Discovery, Live Trends and the ' +
                'configuration are not affected. This cannot be undone.',
            { goBack: 'Cancel', proceed: 'Reset' }
        ).then(function (decision) {
            if (decision !== 'proceed') { return; }

            var environmentId = isAll ? null : vm.selectedTabId;
            BizInterceptorService.resetOperationStats(vm.interceptor.biz_interceptor_id, environmentId)
                .then(function () {
                    $mdToast.show(
                        $mdToast.simple()
                            .textContent('Operation statistics reset successfully')
                            .position('top right')
                            .hideDelay(3000)
                    );
                    refreshFromServer();
                })
                .catch(function (err) {
                    console.error('[ConsolidatedStatsDialog] Error resetting operation stats:', err);
                    dialogService.showErrorDialog(
                        'Reset Failed',
                        (err.data && err.data.message) || 'Could not reset operation statistics.'
                    );
                });
        });
    };

    // Same relative-time treatment as AgentTabController#formatSyncTime
    // (agent details dialog's "Last sync") — a minute-granularity "how long
    // ago" reads faster than an absolute timestamp for a value that's
    // supposed to be recent.
    vm.formatHeartbeat = function (timestamp) {
        if (!timestamp) { return 'Never'; }
        var diff = Date.now() - timestamp;
        var minutes = Math.floor(diff / 60000);
        var hours = Math.floor(minutes / 60);
        var days = Math.floor(hours / 24);
        if (days > 0) { return days + 'd ago'; }
        if (hours > 0) { return hours + 'h ago'; }
        if (minutes > 0) { return minutes + 'm ago'; }
        return 'Just now';
    };

    // Raw "since" timestamp only now — formatting (days/hours/minutes) is
    // <bm-duration>'s job (common/components/duration-badge), the one
    // shared place every such reading in the app goes through instead of
    // each dialog hand-rolling its own days/hours/minutes math.

    // How long this environment's interceptor process has been running —
    // stats.session_started_at, set at the last /init that recovered from
    // an OBSERVED OFFLINE (a fast restart the platform never got a chance
    // to mark OFFLINE just carries this forward unchanged, same as the
    // counters — see BizInterceptorServiceImpl#markOnline) and carried
    // forward unchanged by every /sync since. Deliberately NOT
    // last_state_transition: that one resets on any ONLINE/STANDBY/
    // AGENT_UNREACHABLE/OFFLINE flip, including a transient agent-
    // unreachable blip that never actually restarted the interceptor — using
    // it here made Uptime read as minutes even with a heartbeat count in the
    // thousands. Only meaningful while actually ONLINE — for any other
    // display_status there's no "up" to measure, so null is returned rather
    // than a timestamp that would render as if it still meant that.
    vm.uptimeSince = function (env) {
        return (env && env.display_status === 'ONLINE') ? env.session_started_at : null;
    };

    // Same shape as vm.uptimeSince above, one level down — ri.started_at_ts
    // is the instance-level equivalent of session_started_at (see
    // InterceptorHostContext#getStartedAtTs), same "only reset on a
    // recovery from an OBSERVED OFFLINE, not on every /init" semantics.
    // Only meaningful while the instance is actually RUNNING.
    vm.instanceUptimeSince = function (ri) {
        return (ri && ri.status === 'RUNNING') ? ri.started_at_ts : null;
    };

    // Already the average — computed server-side on the fly over a trailing
    // window (see BizInterceptorServiceImpl#resolveAvgPingTimeMs), at
    // whichever scope tabData carried it in at (interceptor-wide for
    // 'all', that one environment's own for a specific tab). null means no
    // ping sample landed in the window at all.
    vm.avgPingTimeLabel = function (avgPingTimeMs) {
        if (avgPingTimeMs === null || avgPingTimeMs === undefined) { return '—'; }
        return Math.round(avgPingTimeMs) + ' ms';
    };

    // Same shape as vm.avgPingTimeLabel above, for the 0-100 load_pressure
    // gauge (see BizInterceptorServiceImpl#resolveAvgLoadPressure).
    vm.avgLoadPressureLabel = function (avgLoadPressure) {
        if (avgLoadPressure === null || avgLoadPressure === undefined) { return '—'; }
        return Math.round(avgLoadPressure) + '%';
    };

    // Same shape as vm.avgPingTimeLabel/vm.avgLoadPressureLabel above, for
    // the KB/minute throughput metrics (see
    // BizInterceptorServiceImpl#resolveAvgReceiveRateKbMin/
    // #resolveAvgUploadRateKbMin) — replaces the old bmBytes-formatted
    // lifetime totals.
    vm.avgRateLabel = function (kbPerMin) {
        if (kbPerMin === null || kbPerMin === undefined) { return '—'; }
        return kbPerMin.toFixed(1) + ' KB/min';
    };

    // Same shape as vm.avgRateLabel above, for the frames/minute rates (see
    // BizInterceptorServiceImpl#resolveAvgCapturedRatePerMin/
    // #resolveAvgFilteredRatePerMin/#resolveAvgDroppedRatePerMin) — replaces
    // the old Captured/Filtered/Dropped lifetime-total displays.
    vm.avgFrameRateLabel = function (framesPerMin) {
        if (framesPerMin === null || framesPerMin === undefined) { return '—'; }
        return framesPerMin.toFixed(1) + '/min';
    };

    // Both server-computed now (see BizInterceptorServiceImpl#
    // resolveAvgUploadResponsiveness/#resolveAvgHoggingRate) — this
    // controller no longer does any ratio arithmetic itself, just formats
    // the number it's handed, same shape as vm.avgLoadPressureLabel above.
    vm.avgUploadResponsivenessLabel = function (avgUploadResponsiveness) {
        if (avgUploadResponsiveness === null || avgUploadResponsiveness === undefined) { return '—'; }
        return Math.round(avgUploadResponsiveness) + '%';
    };
    // Same 0-100 range/shape as avgUploadResponsivenessLabel above — see
    // InterceptorStats#avgHoggingRate.
    vm.avgHoggingRateLabel = function (avgHoggingRate) {
        if (avgHoggingRate === null || avgHoggingRate === undefined) { return '—'; }
        return Math.round(avgHoggingRate) + '%';
    };

    // Same shape as vm.avgLoadPressureLabel above, for the Active Workers /
    // Transmit Batch Size tiles (see BizInterceptorServiceImpl#
    // resolveWindowedAverages) — no unit suffix, just the rounded count.
    vm.avgActiveWorkerCountLabel = function (avgActiveWorkerCount) {
        if (avgActiveWorkerCount === null || avgActiveWorkerCount === undefined) { return '—'; }
        return Math.round(avgActiveWorkerCount);
    };
    vm.avgTransmitBatchSizeLabel = function (avgTransmitBatchSize) {
        if (avgTransmitBatchSize === null || avgTransmitBatchSize === undefined) { return '—'; }
        return Math.round(avgTransmitBatchSize);
    };

    // Up/down arrow next to the Pressure / Hogging Rate tiles — see
    // BizInterceptorServiceImpl#computeTrend/TrendDirection for how this is
    // decided server-side (moving-average crossover over the last N syncs).
    // UP always means "worse" (rising) regardless of which metric it's
    // attached to. No icon for FLAT (real history, nothing meaningfully
    // moving) or UNKNOWN (not enough history yet) — an icon that's just
    // "neutral" every time isn't worth the visual noise.
    vm.trendIcon = function (trend) {
        if (trend === 'UP') { return 'arrow_upward'; }
        if (trend === 'DOWN') { return 'arrow_downward'; }
        return null;
    };

    vm.cancel = function () { $mdDialog.cancel(); };

    // Opens the full error/stack-trace detail for one environment's
    // agent_connection — the short agent_connection_error is already on the
    // "Down" badge's hover tooltip; this is for when that one line isn't
    // enough to diagnose the problem.
    vm.showAgentConnectionError = function ($event, agentConnection, envName) {
        if ($event) { $event.stopPropagation(); }
        $mdDialog.show({
            controller: 'AgentConnectionErrorDialogController',
            controllerAs: 'vm',
            templateUrl: 'profile/tabs/biz-interceptors/agent-connection-error-dialog/agent-connection-error-dialog.html',
            parent: angular.element(document.body),
            clickOutsideToClose: true,
            escapeToClose: true,
            multiple: true,
            locals: { agentConnection: agentConnection, envName: envName }
        }).catch(angular.noop);
    };

    // An operation's type/method/path/collection_version are STICKY —
    // persisted by the backend the last time it was live-sourced by a Biz
    // Event (see InterceptorOperationStats#type), so they keep showing
    // their last-known value even after the operation stops being live,
    // instead of going blank. `live` (below, vm.isOperationLive) is the
    // actual live/inactive signal — resolved fresh by the backend on each
    // read, never persisted.
    var DIRECTION_LABELS = {
        INBOUND: 'Inbound',
        OUTBOUND: 'Outbound',
        INBOUND_OUTBOUND: 'Inbound + Outbound'
    };
    var DIRECTION_ICONS = {
        INBOUND: 'call_received',
        OUTBOUND: 'call_made',
        INBOUND_OUTBOUND: 'sync_alt'
    };
    vm.directionLabel = function (type) { return DIRECTION_LABELS[type] || 'Unknown'; };
    vm.directionIcon = function (type) { return DIRECTION_ICONS[type] || 'help_outline'; };
    vm.directionClass = function (type) { return 'csd-direction-badge--' + (type ? type.toLowerCase() : 'unknown'); };

    // An operation row is "live" purely off the backend's own `live` flag
    // (see BizInterceptorServiceImpl#withResolvedOperationTypes) — resolved
    // fresh on every read, unlike type/method/path/collection_version which
    // are now sticky (see the comment above). Covers both directions of
    // what used to be two screens: a live operation with zero captured
    // frames yet (what the old Live Endpoints dialog showed), and a
    // captured operation that's no longer live (what this tab always
    // showed) — the latter still shows its full last-known identity, just
    // grayed out (see .csd-row--inactive in the CSS), instead of blanking.
    vm.isOperationLive = function (op) { return !!(op && op.live); };

    // Environment status badge — same 4-state, server-computed
    // display_status as the list view's dot (BizInterceptorsTabController's
    // envDotClass/envDotTooltip), just as a labeled badge instead of a
    // plain dot here.
    var STATUS_BADGE_CLASSES = {
        ONLINE: 'csd-status-badge--online',
        STANDBY: 'csd-status-badge--standby',
        STALLED: 'csd-status-badge--stalled',
        AGENT_UNREACHABLE: 'csd-status-badge--agent-unreachable',
        OFFLINE: 'csd-status-badge--offline'
    };
    var STATUS_BADGE_LABELS = {
        ONLINE: 'Online',
        STANDBY: 'Standby',
        STALLED: 'Stalled',
        AGENT_UNREACHABLE: 'Unreachable',
        OFFLINE: 'Offline'
    };
    vm.statusBadgeClass = function (status) { return STATUS_BADGE_CLASSES[status] || 'csd-status-badge--offline'; };
    vm.statusBadgeLabel = function (status) { return STATUS_BADGE_LABELS[status] || 'Offline'; };
});
